<template>
  <ckeditor
    v-if="isLayoutReady"
    v-model="model"
    :editor="editor"
    :config="config"
    @input="(e) => emit('input', e)"
  />
</template>

<script setup>
import { ref, onMounted } from 'vue'
import {
  ClassicEditor,
  AccessibilityHelp,
  Alignment,
  AutoImage,
  Autosave,
  Bold,
  Essentials,
  Heading,
  ImageBlock,
  ImageInline,
  ImageInsert,
  ImageInsertViaUrl,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Italic,
  Link,
  LinkImage,
  List,
  MediaEmbed,
  Paragraph,
  PasteFromOffice,
  SelectAll,
  SimpleUploadAdapter,
  Underline,
  Undo,
} from 'ckeditor5'
import { Ckeditor } from '@ckeditor/ckeditor5-vue'
import TooUploadAdapter from '/src/utils/TooUploadAdapter.js'

import 'ckeditor5/ckeditor5.css'

const props = defineProps({
  minimalist: {
    type: Boolean,
    default: false,
  },
})

const model = defineModel()
const emit = defineEmits(['input'])

const editor = ClassicEditor
const config = ref(null)
const isLayoutReady = ref(false)

function TooCustomUploadAdapterPlugin(editor) {
  editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
    return new TooUploadAdapter(loader)
  }
}

onMounted(() => {
  if (props.minimalist) {
    config.value = {
      licenseKey: 'GPL',
      toolbar: {
        items: [
          'undo',
          'redo',
          '|',
          'selectAll',
          '|',
          'heading',
          '|',
          'bold',
          'italic',
          'underline',
          '|',
          'link',
          '|',
          'accessibilityHelp',
        ],
        shouldNotGroupWhenFull: false,
      },
      plugins: [
        AccessibilityHelp,
        Autosave,
        Bold,
        Essentials,
        Heading,
        Italic,
        Link,
        Paragraph,
        PasteFromOffice,
        SelectAll,
        Underline,
        Undo,
      ],
      heading: {
        options: [
          {
            model: 'paragraph',
            title: 'Paragraph',
            class: 'ck-heading_paragraph',
          },
        ],
      },
    }
  } else {
    config.value = {
      licenseKey: 'GPL',
      toolbar: {
        items: [
          'undo',
          'redo',
          '|',
          'selectAll',
          '|',
          'heading',
          '|',
          'bold',
          'italic',
          'underline',
          '|',
          'link',
          'insertImage',
          'mediaEmbed',
          '|',
          'alignment',
          '|',
          'bulletedList',
          'numberedList',
          '|',
          'accessibilityHelp',
        ],
        shouldNotGroupWhenFull: false,
      },
      plugins: [
        AccessibilityHelp,
        Alignment,
        AutoImage,
        Autosave,
        Bold,
        Essentials,
        Heading,
        ImageBlock,
        ImageInline,
        ImageInsert,
        ImageInsertViaUrl,
        ImageResize,
        ImageStyle,
        ImageTextAlternative,
        ImageToolbar,
        ImageUpload,
        Italic,
        Link,
        LinkImage,
        List,
        MediaEmbed,
        Paragraph,
        PasteFromOffice,
        SelectAll,
        SimpleUploadAdapter,
        Underline,
        Undo,
        TooCustomUploadAdapterPlugin,
      ],
      image: {
        toolbar: [
          'imageTextAlternative',
          '|',
          'imageStyle:inline',
          'imageStyle:wrapText',
          'imageStyle:breakText',
          '|',
          'resizeImage',
        ],
      },
      link: {
        addTargetToExternalLinks: true,
        defaultProtocol: 'https://',
        decorators: {
          toggleDownloadable: {
            mode: 'manual',
            label: 'Downloadable',
            attributes: {
              download: 'file',
            },
          },
        },
      },
      mediaEmbed: {
        previewsInData: true,
        removeProviders: [
          'instagram',
          'twitter',
          'googleMaps',
          'flickr',
          'facebook',
          'dailymotion',
          'spotify',
        ],
      },
    }
  }

  isLayoutReady.value = true
})
</script>

<style>
@reference '../index.css';

.ck-content > blockquote,
.ck-content > dl,
.ck-content > dd,
.ck-content > h1,
.ck-content > h2,
.ck-content > h3,
.ck-content > h4,
.ck-content > h5,
.ck-content > h6,
.ck-content > hr,
.ck-content > figure,
.ck-content > p,
.ck-content > pre {
  margin: revert;
}

.ck-content > ol,
.ck-content > ul {
  list-style: revert;
  margin: revert;
  padding: revert;
}

.ck-content > table {
  border-collapse: revert;
}

.ck-content > h1,
.ck-content > h2,
.ck-content > h3,
.ck-content > h4,
.ck-content > h5,
.ck-content > h6 {
  font-size: revert;
  font-weight: revert;
}

.ck-content a {
  @apply cursor-pointer text-indigo-700 underline hover:text-indigo-800;
}
</style>
